/**
 * Detects low-quality comments that could be replaced with emoji reactions.
 *
 * Based on https://github.com/refined-github/refined-github/blob/main/source/helpers/is-low-quality-comment.ts
 */

/** Words commonly found in low-quality comments that don't add substantive value. */
export const LOW_QUALITY_WORDS = [
  'a',
  'an',
  'and',
  'any',
  'bump',
  'dito',
  'ditto',
  'following',
  'for',
  'here',
  'is',
  'issue',
  'issues',
  'me',
  'on',
  'please',
  'pls',
  'plz',
  'question',
  'same',
  'solution',
  'still',
  'subscribe',
  'subscribed',
  'there',
  'this',
  'too',
  'update',
  'updates',
] as const

const lowQualityWordsRegex = new RegExp(String.raw`\b(${LOW_QUALITY_WORDS.join('|')})\b`, 'gi')

/** GitHub shortcode emoji that represent simple reactions (e.g., :+1:, :thumbsup:). */
const REACTION_SHORTCODES = [
  ':+1:',
  ':-1:',
  ':thumbsup:',
  ':thumbsdown:',
  ':clap:',
  ':pray:',
  ':ok_hand:',
  ':raised_hands:',
  ':100:',
  ':fire:',
  ':heart:',
  ':eyes:',
  ':rocket:',
  ':tada:',
]

/** Escapes special regex characters in a string. */
const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const shortcodeEmojiRegex = new RegExp(REACTION_SHORTCODES.map(escapeRegex).join('|'), 'gi')

/** Reaction emoji that can be used as simple reactions */
const REACTION_EMOJI = '👍👎👌🙏⬆️'
const reactionEmojiRegex = new RegExp(`[${REACTION_EMOJI}]`, 'gu')

/** Matches vote patterns like +1, -1, +100 */
const VOTE_PATTERN = '[+-]\\d+'
const votePatternRegex = new RegExp(VOTE_PATTERN, 'g')

/** Matches whitespace, punctuation, reaction emoji, skin tone modifiers, vote patterns (+1/-1) */
const nonSubstantiveContentRegex = new RegExp(
  `[\\s,.!?${REACTION_EMOJI}]+|[\\u{1F3FB}-\\u{1F3FF}]|${VOTE_PATTERN}`,
  'gu',
)

/**
 * Returns true if a comment could be replaced with a reaction (e.g., "+1", "me too", "👍").
 */
export function isLowQualityComment(text: string): boolean {
  if (!text || text.trim() === '') return false

  const remainingContent = text
    .replaceAll(lowQualityWordsRegex, '')
    .replaceAll(shortcodeEmojiRegex, '')
    .replaceAll(nonSubstantiveContentRegex, '')

  return remainingContent === ''
}

/**
 * Returns the fragments that triggered low-quality detection.
 */
export function getLowQualityFragments(text: string): string[] {
  if (!text || text.trim() === '') return []

  const fragments: string[] = []

  // Get low-quality words
  const words = text.match(lowQualityWordsRegex) ?? []
  fragments.push(...words.map(w => w.toLowerCase()))

  // Get shortcode emoji
  const shortcodes = text.match(shortcodeEmojiRegex) ?? []
  fragments.push(...shortcodes.map(s => s.toLowerCase()))

  // Get vote patterns
  const votes = text.match(votePatternRegex) ?? []
  fragments.push(...votes)

  // Get reaction emoji
  const emoji = text.match(reactionEmojiRegex) ?? []
  fragments.push(...emoji)

  return [...new Set(fragments)]
}
